import { t } from "i18next";
import { frequentListSchema, ValidationError } from "../models";
import { apiRequest } from "./apiService";
import { sanitizeArray, sanitizeObject, validateArray, validateObject } from "./validationService";

const dbUrl = process.env.REACT_APP_DB_URL;

/**
 * Fetch frequent lists for a user
 */
export const fetchFrequentLists = async (userId) => {
  if (!userId) return [];
  return await apiRequest(`${dbUrl}/frequentLists/${userId}.json`);
};

/**
 * Update frequent lists
 */
export const updateFrequentLists = async (userId, frequentLists) => {
  if (!userId) {
    throw new Error(t("errors.userNotAuthenticated"));
  }

  const errors = validateArray(frequentLists, frequentListSchema);
  if (errors.length > 0) {
    const errorMessage = t("errors.invalidData", { count: errors.length });
    console.error("Validation errors:", errors);
    throw new ValidationError(errorMessage, errors);
  }

  // Sanitize data to ensure it matches the schema
  const sanitizedData = sanitizeArray(frequentLists, frequentListSchema);

  return await apiRequest(`${dbUrl}/frequentLists/${userId}.json`, "PUT", sanitizedData);
};

/**
 * Add a new frequent list
 */
export const addFrequentList = async (userId, frequentList) => {
  if (!userId) {
    throw new Error(t("errors.userNotAuthenticated"));
  }

  const errors = validateObject(frequentList, frequentListSchema);
  if (Object.keys(errors).length > 0) {
    console.error("Validation errors:", errors);
    throw new ValidationError(t("errors.invalidFrequentList"), errors);
  }

  // Sanitize data to ensure it matches the schema
  const sanitizedList = sanitizeObject(frequentList, frequentListSchema);

  let fetchResult = await fetchFrequentLists(userId);
  const existingLists = Array.isArray(fetchResult) ? fetchResult : [];

  const updatedLists = [...existingLists, sanitizedList];

  return await apiRequest(`${dbUrl}/frequentLists/${userId}.json`, "PUT", updatedLists);
};

/**
 * Delete a frequent list
 */
export const deleteFrequentList = async (userId, listIndex) => {
  if (!userId) {
    throw new Error(t("errors.userNotAuthenticated"));
  }

  let fetchResult = await fetchFrequentLists(userId);
  const existingLists = Array.isArray(fetchResult) ? fetchResult : [];

  if (listIndex < 0 || listIndex >= existingLists.length) {
    throw new Error(t("errors.invalidListIndex"));
  }

  const updatedLists = [...existingLists];
  updatedLists.splice(listIndex, 1);

  return await apiRequest(`${dbUrl}/frequentLists/${userId}.json`, "PUT", updatedLists);
};

/**
 * Update a frequent list
 */
export const updateFrequentList = async (userId, listIndex, updatedList) => {
  if (!userId) {
    throw new Error(t("errors.userNotAuthenticated"));
  }

  const errors = validateObject(updatedList, frequentListSchema);
  if (Object.keys(errors).length > 0) {
    console.error("Validation errors:", errors);
    throw new ValidationError(t("errors.invalidFrequentList"), errors);
  }

  // Sanitize the list and preserve timestamps
  const sanitizedList = sanitizeObject(
    {
      ...updatedList,
      lastUsed: Date.now(),
    },
    frequentListSchema,
  );

  let fetchResult = await fetchFrequentLists(userId);
  const existingLists = Array.isArray(fetchResult) ? fetchResult : [];

  if (listIndex < 0 || listIndex >= existingLists.length) {
    throw new Error(t("errors.invalidListIndex"));
  }

  // Update the list at the specified index
  const updatedLists = [...existingLists];
  updatedLists[listIndex] = sanitizedList;

  return await apiRequest(`${dbUrl}/frequentLists/${userId}.json`, "PUT", updatedLists);
};
